import React, { type ReactNode, useEffect, useMemo } from 'react';

import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useChoreographerContext } from '@atlassian/post-office-context/choreographer';
import {
	MessageContextProvider,
	type MessageCategory,
	type MessageContextData,
	PlacementProvider,
	useMessageViewed,
} from '@atlassian/post-office-context/message-lifecycle';

export type { MessageCategory } from '@atlassian/post-office-context/message-lifecycle';

export type PostOfficeMessageCreationType = 'implicit' | 'explicit';

export type PostOfficeMessageProps = {
	/**
	 * The unique ID for this specific delivery of the message.
	 * Used for choreographer concurrency control and viewed analytics.
	 */
	messageInstanceId: string;

	/**
	 * The ID of the message template.
	 * Used for choreographer message type resolution and analytics.
	 */
	messageTemplateId: string;

	/**
	 * The placement ID — used by viewed analytics to identify which placement
	 * this message was rendered in. This is the same `placementId` used in
	 * `usePlacementData`, `useMessageData`, etc.
	 */
	placementId: string;

	/**
	 * The message category — used by the choreographer to determine concurrency rules.
	 * Defaults to 'Engagement' if omitted.
	 */
	messageCategory?: MessageCategory;

	/** The trigger ID associated with this message instance. Used for message instance analytics. */
	triggerId?: string;

	/** Whether this message instance was created implicitly or explicitly. */
	messageCreationType?: PostOfficeMessageCreationType;

	/** Optional journey ID associated with this message instance. */
	journeyId?: string;

	/** Optional journey trigger ID associated with this message instance. */
	journeyTriggerId?: string;

	/** Optional config set ID associated with this message instance. */
	configSetId?: string;

	/**
	 * Bypass the Post Office choreographer for this message.
	 * Defaults to `false` and should almost always stay that way — disabling requires
	 * Post Office team approval.
	 */
	disableChoreographer?: boolean;

	/** Render `null` for tenants in Isolated Cloud environments. */
	hideInIsolatedCloud?: boolean;

	/**
	 * A render prop returning the message UI.
	 * Using a render prop future-proofs the API — Post Office can later inject
	 * context (e.g. dismiss handler, message state) into the function arguments
	 * without breaking existing call sites.
	 * Will not be called if the choreographer blocks this message.
	 */
	children: () => ReactNode;
};

/**
 * Fires the Post Office "viewed" analytics events on mount.
 *
 * This is a separate inner component so that it only mounts when the choreographer
 * allows the message to render. It fires all three viewed events exactly once:
 *   - messageViewed() (legacy in-app lifecycle endpoint)
 *   - messageInstanceViewed()
 *   - messageInstanceDeliveredSuccessfully()
 *
 * Must be rendered inside a MessageContextProvider and PlacementProvider so that
 * useMessageViewed() can read the required context values.
 */
const ViewedOnMount = (): null => {
	const messageViewed = useMessageViewed();

	useEffect(() => {
		void messageViewed();
		// We intentionally omit deps — this should fire exactly once on mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

/**
 * Defers evaluation of the children render prop until the message is mounted.
 *
 * This ensures that any side effects in the render prop only execute when the
 * choreographer allows the message to render, not when it is blocked.
 */
const RenderedChildren = ({ children }: { children: () => ReactNode }): JSX.Element => {
	return <>{children()}</>;
};

const ChoreographedMessage = ({
	children,
	messageId,
	useChoreographedRender,
}: React.PropsWithChildren<{
	messageId: string;
	useChoreographedRender: (args: { messageId: string }) => boolean;
}>): JSX.Element | null => {
	const shouldRender = useChoreographedRender({ messageId });

	return shouldRender ? <>{children}</> : null;
};

/**
 * Render-time Isolated Cloud gate.
 *
 * Returns `null` when the prop is enabled AND the tenant is in an Isolated Cloud
 * environment. Mirrors the headful behaviour wired by
 * `createBasePlacementComponentV2` (`options.hideInIsolatedCloud`).
 *
 * SSR handling is delegated to `isIsolatedCloud()` itself, which falls back to
 * `globalThis.ssrContext?.isInIC` when `document` is undefined.
 */
const IsolatedCloudGate = ({
	children,
	hideInIsolatedCloud,
}: React.PropsWithChildren<{
	hideInIsolatedCloud?: boolean;
}>): JSX.Element | null => {
	if (hideInIsolatedCloud && isIsolatedCloud()) {
		return null;
	}

	return <>{children}</>;
};

/**
 * Choreographer gating using the `ChoreographerContext` provided by
 * `StableChoreographerProvider` (from `@atlassian/post-office-context/choreographer`).
 *
 * When the choreographer is disabled or its factory is not yet loaded,
 * the message renders unconditionally.
 *
 * When `disableChoreographer` is `true`, the gate short-circuits and renders
 * children unconditionally — bypassing the choreographer entirely for this message.
 */
const ChoreographerGate = ({
	children,
	messageId,
	messageTemplateId,
	messageCategory,
	disableChoreographer,
}: React.PropsWithChildren<{
	messageId: string;
	messageTemplateId: string;
	messageCategory?: MessageCategory;
	disableChoreographer?: boolean;
}>): JSX.Element | null => {
	// Hooks must run on every render to preserve hook order — do not short-circuit
	// before calling them, even when `disableChoreographer` is set.
	const { factory: choreographerFactory, choreographerEnabled } = useChoreographerContext();
	const isChoreographedPostOfficeMessagesEnabled = FeatureGates.checkGate(
		'enable_choreographed_post_office_messages',
	);

	if (disableChoreographer) {
		return <>{children}</>;
	}

	if (choreographerFactory && choreographerEnabled && isChoreographedPostOfficeMessagesEnabled) {
		const { shouldBeChoreographed, useChoreographedRender } = choreographerFactory;

		const messageType = messageCategory
			? (messageCategory.toLowerCase() as 'engagement' | 'marketing' | 'transactional')
			: undefined;

		if (shouldBeChoreographed({ messageType })) {
			const useMessageTemplateIdAsChoreographerMessageId = FeatureGates.checkGate(
				'post-office_use_template_id_for_choreo',
			);
			const choreographerMessageId = useMessageTemplateIdAsChoreographerMessageId
				? messageTemplateId
				: messageId;

			return (
				<ChoreographedMessage
					messageId={choreographerMessageId}
					useChoreographedRender={useChoreographedRender}
				>
					{children}
				</ChoreographedMessage>
			);
		}
	}

	return <>{children}</>;
};

/**
 * A stripped-down Post Office message wrapper for headless API consumers.
 *
 * Use this component to wrap your own UI message component when rendering
 * a Post Office message via the headless API (`usePlacementData`, `useMessageData`, etc.).
 *
 * It provides the minimal Post Office infrastructure required for in-product rendering:
 *
 * 1. **Choreographer gating** — integrates with the Post Office choreographer so that
 *    message fatigue and concurrency rules are respected. Returns `null` when the
 *    choreographer blocks this message.
 *
 * 2. **Viewed analytics** — automatically fires `messageViewed`, `messageInstanceViewed`,
 *    and `messageInstanceDeliveredSuccessfully` when the component mounts (i.e., when
 *    the choreographer allows the message to render).
 *
 * **Prerequisites:** This component must be rendered with a
 * `StableChoreographerProvider` ancestor from
 * `@atlassian/post-office-context/choreographer`.
 *
 * @example
 * ```tsx
 * const { message } = useMessageData({
 *   placementId: 'my-placement',
 *   messageTemplateId: 'my-template',
 * });
 *
 * if (!message) return null;
 *
 * return (
 *   <PostOfficeMessage
 *     messageInstanceId={message.messageInstanceId}
 *     messageTemplateId={message.messageTemplateId}
 *     placementId="my-placement"
 *   >
 *     {() => <MySpotlightComponent content={message.content} />}
 *   </PostOfficeMessage>
 * );
 * ```
 */
export const PostOfficeMessage = ({
	messageInstanceId,
	messageTemplateId,
	placementId,
	messageCategory,
	triggerId,
	messageCreationType,
	journeyId,
	journeyTriggerId,
	configSetId,
	disableChoreographer,
	hideInIsolatedCloud,
	children,
}: PostOfficeMessageProps): JSX.Element | null => {
	const messageContext: MessageContextData = useMemo(
		() => ({
			messageInstanceId,
			messageTemplateId,
			messageCategory,
			triggerId,
			messageCreationType,
			journeyId,
			journeyTriggerId,
			configSetId,
		}),
		[
			messageInstanceId,
			messageTemplateId,
			messageCategory,
			triggerId,
			messageCreationType,
			journeyId,
			journeyTriggerId,
			configSetId,
		],
	);

	const placementContext = useMemo(() => ({ placementId }), [placementId]);

	return (
		<IsolatedCloudGate hideInIsolatedCloud={hideInIsolatedCloud}>
			<PlacementProvider value={placementContext}>
				<MessageContextProvider value={messageContext}>
					<ChoreographerGate
						messageId={messageInstanceId}
						messageTemplateId={messageTemplateId}
						messageCategory={messageCategory}
						disableChoreographer={disableChoreographer}
					>
						<ViewedOnMount />
						<RenderedChildren>{children}</RenderedChildren>
					</ChoreographerGate>
				</MessageContextProvider>
			</PlacementProvider>
		</IsolatedCloudGate>
	);
};
